import { MixedObject } from '@core/interfaces/mixed-object';

export type ProgressFn = (loaded: number, total: number) => void;

interface DownloadWithProgressParams {
  url: string,
  filename: string,
  onProgress?: ProgressFn,
  method?: string,
  requestBody?: MixedObject
}

const EXTENSION_BY_TYPE = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
};

export function downloadAndSaveFileWithProgress(params: DownloadWithProgressParams) {
  let {method, onProgress} = params;
  const {url, filename, requestBody} = params;
  if (!method) {
    method = 'get';
  }
  if (!onProgress) {
    onProgress = () => void 0;
  }
  let filenameToSave: string;
  if (!filename) {
    const urlParts = url.split('/');
    filenameToSave = urlParts[urlParts.length - 1];
  } else {
    filenameToSave = filename;
  }

  let body: FormData | null = null;
  if (requestBody) {
    body = new FormData();

    for (const key in requestBody) {
      if (Object.prototype.hasOwnProperty.call(requestBody, key)) {
        body.append(key, requestBody[key]);
      }
    }
  }
  try {
    fetch(url, {
      method,
      body,
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }

        let type = response.headers.get('Content-Type');
        if (type) {
          if (type.includes(';')) {
            type = type.split(';')[0];
          }
          const extension = EXTENSION_BY_TYPE[type] || type.split('/')[1];
          if (extension) {
            if (!filenameToSave.endsWith(`.${extension}`)) {
              filenameToSave += `.${extension}`;
            }
          }
        }
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const filenameFromHeader = contentDisposition.split('filename=')[1];
          if (filenameFromHeader) {
            filenameToSave = filenameFromHeader.replace(/["']/g, '');
          }
        }

        // Sprawdź rozmiar pliku
        const contentLength = response.headers.get('Content-Length') || '0';

        const totalSize = parseInt(contentLength, 10);
        let loadedSize = 0;

        // Stwórz czytnik strumienia odpowiedzi
        const reader = response.body!.getReader();
        return new ReadableStream({
          start(controller) {
            function push() {
              reader.read().then(({done, value}) => {
                if (done) {
                  controller.close();
                  return;
                }

                if (contentLength) {
                  loadedSize += value.length;
                  onProgress(loadedSize, totalSize);
                }

                controller.enqueue(value);
                push();
              });
            }

            push();
          },
        });
      })
      .then(stream => {
        // Konwertuj strumień na obiekt Blob
        const newResponse = new Response(stream);
        return newResponse.blob();
      })
      .then(blob => {
        // Utwórz obiekt URL dla tego blob
        const blobUrl = URL.createObjectURL(blob);

        // Utwórz element <a> do pobrania pliku
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filenameToSave;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      });
  } catch (error) {
    console.error('Error downloading or saving file:', error);
  }
}
